import { alpha, Box, IconButton } from '@chronosphereio/chrono-ui';
import ChevronLeft from 'mdi-material-ui/ChevronLeft';
import ChevronRight from 'mdi-material-ui/ChevronRight';

// Wide enough that the chevron sits on a visibly solid backdrop (see
// `SOLID_PLATEAU_PCT`), narrow enough not to eat into the canvas.
const RAIL_WIDTH_PX = 56;

// Portion of the rail at full color before the fade. With the chevron at
// the rail's 50% mark, 30% puts it well inside the solid plateau on its
// outer side and only ~28% into the fade on its inner side.
const SOLID_PLATEAU_PCT = 30;

/**
 * Class the cluster wrapper uses to toggle visibility on hover/focus-within.
 */
export const DEPENDENCY_MAP_PAN_BUTTON_CLASS = 'DependencyMap-panButton';

interface DependencyMapPanButtonProps {
  side: 'left' | 'right';
  onClick: () => void;
}

/**
 * Full-height edge rail with a centered chevron. The rail's gradient is the
 * actionable cousin of the passive `EdgeFade` cue, so at-rest fade and the
 * hover-revealed pan affordance read as the same visual language.
 *
 * Rail is `pointerEvents: none` so it doesn't shadow node/link hover events
 * on the SVG behind it; the inner IconButton re-enables them for the click.
 *
 * Caller mounts only on sides where content is clipped, so no disabled state
 * is needed.
 */
export const DependencyMapPanButton = ({ side, onClick }: DependencyMapPanButtonProps) => (
  <Box
    className={DEPENDENCY_MAP_PAN_BUTTON_CLASS}
    sx={(theme) => {
      const color = theme.palette.background.container;
      // Same fade direction as `EdgeFade` so the rail extends the fade
      // rather than fighting it. Solid plateau keeps the chevron readable.
      const gradientDirection = side === 'left' ? 'to right' : 'to left';
      return {
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side]: 0,
        width: RAIL_WIDTH_PX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: side === 'left' ? 'start' : 'end',
        background: `linear-gradient(${gradientDirection}, ${color} ${SOLID_PLATEAU_PCT}%, ${alpha(color, 0)} 100%)`,
        pointerEvents: 'none',
        zIndex: 1,
      };
    }}
  >
    <IconButton
      size="small"
      onClick={onClick}
      aria-label={side === 'left' ? 'Pan left' : 'Pan right'}
      sx={{ pointerEvents: 'auto' }}
    >
      {side === 'left' ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
    </IconButton>
  </Box>
);
